import React, { useEffect, useState, useCallback } from 'react';
import { ArrowPathIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { useBillingStore } from '../../store/billingStore';
import { useBilling } from '../../hooks/useBilling';

interface Props {
  billId: string;
}

const InvoicePreviewTab: React.FC<Props> = ({ billId }) => {
  const { getInvoicePreviewUrl, downloadInvoicePdf, fetchThemes } = useBilling();
  const themes = useBillingStore((s) => s.themes);
  const [selectedThemeId, setSelectedThemeId] = useState('');
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  // Load themes if not yet loaded
  useEffect(() => {
    if (themes.length === 0) {
      fetchThemes().catch(() => {});
    }
  }, []);

  const loadPreview = useCallback(async () => {
    setLoadingPreview(true);
    setPreviewError(null);
    try {
      const url = getInvoicePreviewUrl(billId, selectedThemeId || undefined);
      const res = await fetch(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('webelieve_access_token') || ''}`,
        },
      });
      if (!res.ok) throw new Error('Failed to load preview');
      const html = await res.text();
      setPreviewHtml(html);
    } catch (err) {
      setPreviewError(err instanceof Error ? err.message : 'Failed to load preview');
    } finally {
      setLoadingPreview(false);
    }
  }, [billId, selectedThemeId, getInvoicePreviewUrl]);

  // Load preview on mount and theme change
  useEffect(() => {
    loadPreview();
  }, [billId, selectedThemeId]);

  const handleDownload = () => {
    downloadInvoicePdf(billId, selectedThemeId || undefined).catch(() => {});
  };

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Theme:</label>
          <select
            value={selectedThemeId}
            onChange={(e) => setSelectedThemeId(e.target.value)}
            className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
          >
            <option value="">Default Theme</option>
            {themes.map((t) => (
              <option key={t.theme_id} value={t.theme_id}>
                {t.name}{t.is_default ? ' (Default)' : ''}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={loadPreview}
            disabled={loadingPreview}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            <ArrowPathIcon className={`h-4 w-4 ${loadingPreview ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={handleDownload}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-lex-accent text-lex-accent-contrast rounded-lg hover:bg-lex-accent-hover transition-colors"
          >
            <ArrowDownTrayIcon className="h-4 w-4" />
            Download PDF
          </button>
        </div>
      </div>

      {/* Preview */}
      {previewError && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4">
          <p className="text-sm text-red-800 dark:text-red-200">{previewError}</p>
        </div>
      )}

      {loadingPreview && !previewHtml && (
        <div className="flex items-center justify-center py-20">
          <div className="h-6 w-6 border-2 border-lex-accent border-t-transparent rounded-full animate-spin" />
          <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">Loading preview...</span>
        </div>
      )}

      {previewHtml && (
        <div className="bg-white rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm">
          <iframe
            srcDoc={previewHtml}
            title="Invoice Preview"
            className="w-full border-0"
            style={{ minHeight: '800px', height: '100%' }}
            sandbox="allow-same-origin"
          />
        </div>
      )}
    </div>
  );
};

export default InvoicePreviewTab;
